import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getStoredUser, parseJsonSafe } from '../api/client';
import {
  getMyPracticeCurriculum,
  getMyStudentDashboard,
  getMyTutorStudents,
  getStudentPracticeCurriculum,
} from '../api/me';
import {
  buildPracticeSessionPayload,
  getSessions,
  postCreateSession,
  postCreateStudentPracticeSession,
} from '../api/tutor';
import {
  getSessionId,
  getSessionRuntime,
  getSessionRuntimePath,
} from '../lib/sessionRouting';
import ActiveSessionConflictPrompt from '../components/ActiveSessionConflictPrompt';
import useActiveSessionConflict, {
  createSessionRequestError,
} from '../components/useActiveSessionConflict';
import SessionLengthSelector from '../components/SessionLengthSelector';
import useModalFocusTrap from '../components/useModalFocusTrap';
import { GUARDIAN_UNLOCK_REQUIREMENT, STAFF_GUARDIAN_NEXT_STEP } from '../copy/onboardingStatus';
import { getRecentSessionSummary, getRecentSessionTitle } from '../utils/sessionSummaries';
import { DEFAULT_SESSION_LENGTH } from '../utils/sessionLength';
import {
  PRACTICE_SUBJECTS,
  getAllPracticeTopics,
  getPracticeAudienceCopy,
  getPracticeRecommendations,
  getPracticeStarterSuggestions,
  getSelectionModeUnavailableCopy,
} from './practiceCatalog';

const BUILDER_UNITS = ['U1', 'U2', 'U3'];
const BUILDER_SCOPE_OPTIONS = [
  {
    key: 'topic_group',
    label: 'Topic group',
    description: 'Build a mixed session inside one topic group.',
  },
  {
    key: 'multi_topic',
    label: 'Multiple topics',
    description: 'Choose several exact topics and mix them into one session.',
  },
  {
    key: 'unit_mix',
    label: 'Unit mix',
    description: 'Create a broader mixed session from one unit.',
  },
  {
    key: 'review_mix',
    label: 'Review mix',
    description: 'Let the system build a session from review-due and weaker areas.',
  },
];

// Per-mode availability is supplied by the backend on the curriculum payload
// (`selection_modes`). The client must never infer the deployment environment
// for itself, so a missing row is NOT treated as "available" -- an older
// backend or a failed fetch degrades to unavailable. `single_topic` is the one
// exception: it is the only route to any practice at all, so it stays
// reachable rather than locking the learner out of the whole page.
const ALWAYS_REACHABLE_SELECTION_MODES = new Set(['single_topic']);
const UNKNOWN_SELECTION_MODE_REASON = 'CAPABILITY_UNKNOWN';

function readSelectionModeAvailability(curriculum) {
  const rows = Array.isArray(curriculum?.selection_modes) ? curriculum.selection_modes : [];
  const availabilityByMode = new Map();
  rows.forEach((row) => {
    const mode = typeof row?.selection_mode === 'string' ? row.selection_mode.trim() : '';
    if (!mode || availabilityByMode.has(mode)) {
      return;
    }
    const isAvailable = row?.available === true;
    const rawReason = typeof row?.unavailable_reason === 'string' ? row.unavailable_reason.trim() : '';
    availabilityByMode.set(mode, {
      available: isAvailable,
      unavailableReason: isAvailable ? null : (rawReason || UNKNOWN_SELECTION_MODE_REASON),
    });
  });
  return availabilityByMode;
}

function getSelectionModeState(availabilityByMode, mode) {
  const entry = availabilityByMode.get(mode);
  if (entry) {
    return entry;
  }
  if (ALWAYS_REACHABLE_SELECTION_MODES.has(mode)) {
    return { available: true, unavailableReason: null };
  }
  return { available: false, unavailableReason: UNKNOWN_SELECTION_MODE_REASON };
}

function formatCurriculumRecency(value, isColdStart) {
  if (!value) {
    return isColdStart ? 'No practice yet' : 'Not practised yet';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return isColdStart ? 'No practice yet' : 'Not practised yet';
  }

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfValueDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday - startOfValueDay) / 86400000);

  if (diffDays <= 0) {
    return 'Last practised today';
  }
  if (diffDays === 1) {
    return 'Last practised yesterday';
  }
  return `Last practised ${diffDays} days ago`;
}

function getStatusClass(status) {
  switch (status) {
    case 'STRONG':
      return 'practice-status-strong';
    case 'IN_PROGRESS':
      return 'practice-status-progress';
    case 'NEEDS_REVIEW':
      return 'practice-status-review';
    default:
      return 'practice-status-progress';
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'STRONG':
      return 'Strong';
    case 'IN_PROGRESS':
      return 'In progress';
    case 'NEEDS_REVIEW':
      return 'Needs review';
    default:
      return '';
  }
}

function getSessionStatusTone(status) {
  if (status === 'COMPLETED') {
    return 'weekly-practice-plan-pill-complete';
  }
  return 'weekly-practice-plan-pill-progress';
}

function extractErrorMessage(data, fallback) {
  return (
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.message === 'string' && data.message) ||
    fallback
  );
}

function getSessionStartErrorMessage(error) {
  if (Number(error?.status) === 403) {
    return 'This topic is not available for practice yet. Choose another available topic.';
  }
  return error?.message || 'Failed to start practice.';
}

function getPracticeTargetKey(skillCode, microSkillCode, fallback = '') {
  const normalizedSkillCode = typeof skillCode === 'string' ? skillCode.trim() : '';
  const normalizedMicroSkillCode = typeof microSkillCode === 'string' ? microSkillCode.trim() : '';
  return [normalizedSkillCode, normalizedMicroSkillCode].filter(Boolean).join(':') || String(fallback || '');
}

function isBetaReadyItem(item) {
  if (Object.prototype.hasOwnProperty.call(item || {}, 'beta_exposed')) {
    return item?.beta_exposed === true;
  }
  return item?.beta_ready === true || item?.betaStatus === true;
}

function getBetaStatusLabel(item) {
  if (typeof item?.beta_status_label === 'string' && item.beta_status_label.trim()) {
    return item.beta_status_label.trim();
  }
  if (typeof item?.betaStatusLabel === 'string' && item.betaStatusLabel.trim()) {
    return item.betaStatusLabel.trim();
  }
  return isBetaReadyItem(item) ? 'Validated for Beta' : 'In Evaluation';
}

function splitCurriculumByBetaReadiness(areas = []) {
  const availableAreas = [];
  const upcomingAreas = [];

  areas.forEach((area) => {
    const availableGroups = [];
    const upcomingGroups = [];
    (Array.isArray(area.groups) ? area.groups : []).forEach((group) => {
      const availableSkills = [];
      const upcomingSkills = [];
      (Array.isArray(group.skills) ? group.skills : []).forEach((skill) => {
        if (isBetaReadyItem(skill)) {
          availableSkills.push(skill);
        } else {
          upcomingSkills.push(skill);
        }
      });
      if (availableSkills.length) {
        availableGroups.push({
          ...group,
          beta_ready: true,
          beta_exposed: true,
          beta_status: 'beta_stable',
          beta_status_label: 'Validated for Beta',
          skills: availableSkills,
        });
      }
      if (upcomingSkills.length) {
        upcomingGroups.push({
          ...group,
          beta_ready: false,
          beta_exposed: false,
          beta_status: group.beta_status || 'in_evaluation',
          beta_status_label: getBetaStatusLabel(group),
          skills: upcomingSkills,
        });
      }
    });
    if (availableGroups.length) {
      availableAreas.push({ ...area, groups: availableGroups });
    }
    if (upcomingGroups.length) {
      upcomingAreas.push({ ...area, groups: upcomingGroups });
    }
  });

  return { availableAreas, upcomingAreas };
}

function getRecommendationPresentation(key) {
  switch (key) {
    case 'review':
      return {
        subjectLabel: 'Review due',
        status: 'NEEDS_REVIEW',
        recommendationStatus: 'Needs review',
      };
    case 'prerequisite_refresh':
      return {
        subjectLabel: 'Support first',
        status: 'IN_PROGRESS',
        recommendationStatus: 'In progress',
      };
    case 'independent_practice':
      return {
        subjectLabel: 'Building forward',
        status: 'IN_PROGRESS',
        recommendationStatus: 'In progress',
      };
    case 'focus':
    default:
      return {
        subjectLabel: 'System guidance',
        status: 'IN_PROGRESS',
        recommendationStatus: 'In progress',
      };
  }
}

function normalizeDashboardRecommendations(studentDashboard) {
  const nextAction = studentDashboard?.next_action;
  const weeklyPlanItems = Array.isArray(studentDashboard?.weekly_plan?.items)
    ? studentDashboard.weekly_plan.items
    : [];
  const recommendations = [];
  const seenSkillCodes = new Set();

  const pushRecommendation = ({
    skillCode,
    focusName,
    subjectLabel,
    status,
    recommendationStatus,
    reason,
    tier = 'FOUNDATION',
  }) => {
    const normalizedSkillCode = typeof skillCode === 'string' ? skillCode.trim() : '';
    const normalizedFocusName = typeof focusName === 'string' ? focusName.trim() : '';
    if (!normalizedSkillCode || !normalizedFocusName || seenSkillCodes.has(normalizedSkillCode)) {
      return;
    }
    seenSkillCodes.add(normalizedSkillCode);
    recommendations.push({
      skillCode: normalizedSkillCode,
      focusName: normalizedFocusName,
      subjectLabel,
      status,
      recommendationStatus,
      reason,
      tier,
    });
  };

  if (nextAction?.focus_area && nextAction?.recommended_skill_code) {
    pushRecommendation({
      skillCode: nextAction.recommended_skill_code,
      focusName: String(nextAction.focus_area),
      subjectLabel: 'System guidance',
      status: 'IN_PROGRESS',
      recommendationStatus: 'In progress',
      reason: String(nextAction.reason || 'This is the clearest next step from recent practice.'),
    });
  }

  weeklyPlanItems.forEach((item) => {
    if (item?.status !== 'IN_PROGRESS') {
      return;
    }
    const recommendation = getRecommendationPresentation(String(item?.key || ''));
    const skillLabels = Array.isArray(item?.skill_labels) ? item.skill_labels : [];
    pushRecommendation({
      skillCode: item?.recommended_skill_code,
      focusName: String(skillLabels[0] || item?.title || '').trim(),
      subjectLabel: recommendation.subjectLabel,
      status: recommendation.status,
      recommendationStatus: recommendation.recommendationStatus,
      reason: String(item?.detail || 'This is a useful area to practise next.'),
    });
  });

  return recommendations.slice(0, 4);
}

function buildPracticePageState({
  role,
  studentDashboard,
  recentSession,
  sessions,
}) {
  const starterSuggestions = getPracticeStarterSuggestions();
  const fallbackRecommendations = getPracticeRecommendations();
  const skillBuckets = Array.isArray(studentDashboard?.skill_progress?.buckets)
    ? studentDashboard.skill_progress.buckets
    : [];
  const bucketEvidence = skillBuckets.some((bucket) => Number(bucket?.count || 0) > 0);
  const recentSessions = Array.isArray(studentDashboard?.recent_sessions) ? studentDashboard.recent_sessions : [];
  const hasHistorySignals = Boolean(
    studentDashboard?.next_action?.focus_area
    || bucketEvidence
    || recentSessions.length
    || recentSession
    || (Array.isArray(sessions) && sessions.length > 0)
  );

  if (role === 'STUDENT') {
    const recommendations = normalizeDashboardRecommendations(studentDashboard);
    const hasRecommendations = recommendations.length > 0;
    const isColdStart = !hasRecommendations;

    return {
      isColdStart,
      hasRecommendations,
      // Resume availability is a property of the session, not of how much
      // history the student has. A brand-new student with an ACTIVE session has
      // no recommendations yet and still must be able to get back into it
      // (docs/01_product/session_lifecycle.md:577-581).
      hasRecentSession: Boolean(recentSession),
      recommendations,
      starterSuggestions,
    };
  }

  const isColdStart = !hasHistorySignals;
  return {
    isColdStart,
    hasRecommendations: !isColdStart,
    hasRecentSession: Boolean(recentSession),
    recommendations: fallbackRecommendations,
    starterSuggestions,
  };
}

function RecommendationCards({ items, pendingSelectionKey, onStartPractice }) {
  return (
    <div className="practice-recommendation-grid">
      {items.map((skill) => (
        <article key={`${skill.skillCode}-${skill.focusName}`} className="practice-recommendation-card">
          <div className="practice-card-header">
            <div>
              <p className="practice-card-kicker">{skill.subjectLabel}</p>
              <h3>{skill.focusName}</h3>
            </div>
            <span className={`status-pill ${getStatusClass(skill.status)}`}>{skill.recommendationStatus}</span>
          </div>
          <p>{skill.reason}</p>
          <div className="practice-recommendation-card-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onStartPractice(skill)}
              disabled={pendingSelectionKey === skill.skillCode}
            >
              {pendingSelectionKey === skill.skillCode ? 'Starting...' : 'Continue practice'}
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function StarterCards({ items, pendingSelectionKey, onStartPractice }) {
  return (
    <div className="practice-recommendation-grid">
      {items.map((skill) => (
        <article key={`${skill.skillCode}-${skill.focusName}`} className="practice-recommendation-card">
          <div className="practice-card-header">
            <div>
              <p className="practice-card-kicker">{skill.subjectLabel}</p>
              <h3>{skill.focusName}</h3>
            </div>
          </div>
          <p>{skill.starterReason}</p>
          <div className="practice-recommendation-card-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onStartPractice(skill)}
              disabled={pendingSelectionKey === skill.skillCode}
            >
              {pendingSelectionKey === skill.skillCode ? 'Starting...' : 'Start here'}
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function PracticeBuilderModeSwitch({ value, onChange }) {
  return (
    <div className="practice-builder-mode-switch" role="tablist" aria-label="Practice builder mode">
      {[
        { key: 'exact', label: 'Exact topic' },
        { key: 'build', label: 'Build session' },
      ].map((mode) => (
        <button
          key={mode.key}
          type="button"
          role="tab"
          aria-selected={value === mode.key}
          className={`practice-builder-mode-tab${value === mode.key ? ' is-active' : ''}`}
          onClick={() => onChange(mode.key)}
        >
          {mode.label}
        </button>
      ))}
    </div>
  );
}

function TierSelector({ value, onChange }) {
  const options = [
    {
      key: 'FOUNDATION',
      label: 'Foundation',
      description: 'Stay within foundation-accessible questions and methods.',
    },
    {
      key: 'HIGHER',
      label: 'Higher',
      description: 'Use higher-tier questions for broader challenge and depth.',
    },
  ];
  return (
    <fieldset className="practice-builder-fieldset">
      <legend>Tier</legend>
      <div className="practice-builder-option-grid practice-builder-option-grid-tight">
        {options.map((tier) => (
          <button
            key={tier.key}
            type="button"
            className={`practice-builder-option practice-builder-option-compact${value === tier.key ? ' is-active' : ''}`}
            aria-pressed={value === tier.key}
            onClick={() => onChange(tier.key)}
          >
            <strong>{tier.label}</strong>
            <span>{tier.description}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function CurriculumSelectorDialog({
  isOpen,
  isLoading,
  areas,
  upcomingAreas = [],
  canPreviewUpcoming = false,
  isColdStart,
  pendingSelectionKey,
  canStartPractice,
  actionError,
  onClose,
  onStartPractice,
  restoreFocusRef,
}) {
  const [selectedAreaKey, setSelectedAreaKey] = useState('');
  const [selectedGroupKey, setSelectedGroupKey] = useState('');
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useModalFocusTrap({
    active: isOpen,
    containerRef: dialogRef,
    initialFocusRef: closeButtonRef,
    restoreFocusRef,
  });

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedAreaKey('');
      setSelectedGroupKey('');
      return;
    }
    if (!areas.length) {
      setSelectedAreaKey('');
      setSelectedGroupKey('');
      return;
    }

    const hasSelectedArea = areas.some((area) => area.key === selectedAreaKey);
    if (!hasSelectedArea) {
      setSelectedAreaKey(areas[0].key || '');
    }
  }, [areas, isOpen, selectedAreaKey]);

  const selectedArea = useMemo(
    () => areas.find((area) => area.key === selectedAreaKey) || null,
    [areas, selectedAreaKey],
  );
  const selectedGroups = useMemo(
    () => (Array.isArray(selectedArea?.groups) ? selectedArea.groups : []),
    [selectedArea],
  );
  
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!selectedGroups.length) {
      setSelectedGroupKey('');
      return;
    }

    const hasSelectedGroup = selectedGroups.some((group) => group.key === selectedGroupKey);
    if (!hasSelectedGroup) {
      setSelectedGroupKey(selectedGroups[0].key || '');
    }
  }, [isOpen, selectedGroupKey, selectedGroups]);

  if (!isOpen) {
    return null;
  }

  const selectedGroup = selectedGroups.find((group) => group.key === selectedGroupKey) || null;
  const selectedSkills = Array.isArray(selectedGroup?.skills) ? selectedGroup.skills : [];

  return (
    <div className="practice-curriculum-modal-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        className="practice-curriculum-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="practice-curriculum-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="practice-curriculum-header">
          <div>
            <p className="eyebrow">Full curriculum</p>
            <h2 id="practice-curriculum-title">Select from full curriculum</h2>
            <p>
              Choose from topics currently available in Beta without leaving the guided practice flow.
            </p>
          </div>
          <button ref={closeButtonRef} type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        {actionError ? <p role="alert" className="notice notice-error">{actionError}</p> : null}

        {isLoading ? (
          <div className="practice-curriculum-empty">
            <p>Loading full curriculum...</p>
          </div>
        ) : !areas.length ? (
          <div className="practice-curriculum-empty">
            <p>No Beta-ready topics are available yet.</p>
          </div>
        ) : (
          <>
            <p className="practice-beta-scope-note">Currently available in Beta. More topics are being added regularly.</p>
            <div className="practice-curriculum-layout">
              <section className="practice-curriculum-step">
                <div className="practice-curriculum-step-header">
                  <span>Step 1</span>
                  <h3>Area</h3>
                </div>
                <div className="practice-curriculum-option-list">
                  {areas.map((area) => (
                    <button
                      key={area.key}
                      type="button"
                      className={`practice-curriculum-option${selectedArea?.key === area.key ? ' is-active' : ''}`}
                      aria-pressed={selectedArea?.key === area.key}
                      onClick={() => {
                        setSelectedAreaKey(area.key);
                        setSelectedGroupKey(area.groups?.[0]?.key || '');
                      }}
                    >
                      <span className="practice-curriculum-option-title">{area.label}</span>
                      {area.description ? (
                        <span className="practice-curriculum-option-description">{area.description}</span>
                      ) : null}
                      <span className="practice-curriculum-option-meta">{area.groups?.length || 0} topic groups</span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="practice-curriculum-step">
                <div className="practice-curriculum-step-header">
                  <span>Step 2</span>
                  <h3>Topic group</h3>
                </div>
                <div className="practice-curriculum-option-list" key={selectedArea?.key || 'no-area'}>
                  {selectedGroups.map((group) => (
                    <button
                      key={group.key}
                      type="button"
                      className={`practice-curriculum-option${selectedGroup?.key === group.key ? ' is-active' : ''}`}
                      aria-pressed={selectedGroup?.key === group.key}
                      onClick={() => setSelectedGroupKey(group.key)}
                    >
                      <span className="practice-curriculum-option-title">{group.label}</span>
                      {group.description ? (
                        <span className="practice-curriculum-option-description">{group.description}</span>
                      ) : null}
                      <span className="practice-curriculum-option-meta">{group.skills?.length || 0} topics</span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="practice-curriculum-step practice-curriculum-step-skills">
                <div className="practice-curriculum-step-header">
                  <span>Step 3</span>
                  <h3>Topics and micro-skills</h3>
                </div>
                <div
                  key={`${selectedArea?.key || 'no-area'}:${selectedGroup?.key || 'no-group'}`}
                  className="practice-curriculum-skill-list"
                >
                  {selectedSkills.map((skill) => {
                    const skillCode = skill.skillCode || skill.skill_code;
                    const microSkillCode = skill.microSkillCode || skill.micro_skill_code;
                    const selectionKey = getPracticeTargetKey(skillCode, microSkillCode, skill.name);
                    const status = skill.status || null;
                    const lastPractised = formatCurriculumRecency(skill.lastPractisedAt || skill.last_practiced_at, isColdStart);
                    return (
                      <article key={selectionKey} className="practice-curriculum-skill-item">
                        <div className="practice-skill-copy">
                          <div className="practice-card-header">
                            <div>
                              <strong>{skill.name}</strong>
                              <p className="practice-card-kicker">{skill.macroName || skill.macro_name}</p>
                            </div>
                            <span className="status-pill status-pill-marked">{getBetaStatusLabel(skill)}</span>
                            {status ? (
                              <span className={`status-pill ${getStatusClass(status)}`}>{getStatusLabel(status)}</span>
                            ) : null}
                          </div>
                          {skill.description ? (
                            <p className="practice-curriculum-skill-description">{skill.description}</p>
                          ) : null}
                          <p className="muted-text">{lastPractised}</p>
                        </div>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => onStartPractice({
                            skillCode,
                            microSkillCode,
                            tier: skill.tier,
                            focusName: skill.name,
                          })}
                          disabled={pendingSelectionKey === selectionKey || !canStartPractice}
                        >
                          {pendingSelectionKey === selectionKey ? 'Starting...' : 'Start practice'}
                        </button>
                      </article>
                    );
                  })}
                </div>
              </section>
            </div>
            {canPreviewUpcoming && upcomingAreas.length ? (
              <details className="practice-upcoming-topics">
                <summary>Upcoming topics</summary>
                <p className="muted-text">These areas are in evaluation and are not selectable for Beta sessions yet.</p>
                {upcomingAreas.map((area) => (
                  <section key={area.key} className="practice-upcoming-area">
                    <h3>{area.label}</h3>
                    {(area.groups || []).map((group) => (
                      <div key={group.key} className="practice-upcoming-group">
                        <strong>{group.label}</strong>
                        <span className="status-pill status-pill-info">{getBetaStatusLabel(group)}</span>
                      </div>
                    ))}
                  </section>
                ))}
              </details>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function PracticePage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const role = user?.role || null;
  const isTutor = role === 'TUTOR';
  const isStudent = role === 'STUDENT';
  const audienceCopy = getPracticeAudienceCopy(role);
  const allTopics = useMemo(() => getAllPracticeTopics(), []);
  const [selectedSubjectKey, setSelectedSubjectKey] = useState(PRACTICE_SUBJECTS[0]?.key || '');
  const [recentSession, setRecentSession] = useState(null);
  const [recentSessionsList, setRecentSessionsList] = useState([]);
  const [recentSessionError, setRecentSessionError] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [isLoadingStudents, setIsLoadingStudents] = useState(isTutor);
  const [studentDashboard, setStudentDashboard] = useState(null);
  const [isLoadingStudentDashboard, setIsLoadingStudentDashboard] = useState(isStudent);
  const [fullCurriculum, setFullCurriculum] = useState(null);
  const [isLoadingCurriculum, setIsLoadingCurriculum] = useState(isStudent);
  const [actionError, setActionError] = useState('');
  const [pendingSelectionKey, setPendingSelectionKey] = useState('');
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);
  const [isAllTopicsOpen, setIsAllTopicsOpen] = useState(false);
  const [isCurriculumSelectorOpen, setIsCurriculumSelectorOpen] = useState(false);
  const curriculumTriggerRef = useRef(null);
  const [builderMode, setBuilderMode] = useState('exact');
  const [exactSessionLength, setExactSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const [buildSessionLength, setBuildSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const [buildSelectionMode, setBuildSelectionMode] = useState('topic_group');
  const [buildTier, setBuildTier] = useState('FOUNDATION');
  const [buildUnit, setBuildUnit] = useState('U1');
  const [buildAreaKey, setBuildAreaKey] = useState('');
  const [buildGroupKey, setBuildGroupKey] = useState('');
  const [selectedTopicTargets, setSelectedTopicTargets] = useState([]);

  const rawPageState = buildPracticePageState({
    role,
    studentDashboard,
    recentSession,
    sessions: recentSessionsList,
  });
  const curriculumAreas = useMemo(
    () => (Array.isArray(fullCurriculum?.areas) ? fullCurriculum.areas : []),
    [fullCurriculum],
  );
  const { availableAreas: betaReadyCurriculumAreas, upcomingAreas } = useMemo(
    () => splitCurriculumByBetaReadiness(curriculumAreas),
    [curriculumAreas],
  );
  const betaReadySkillCodes = useMemo(() => new Set(
    betaReadyCurriculumAreas.flatMap((area) => (
      area.groups || []
    ).flatMap((group) => (
      group.skills || []
    ).map((skill) => String(skill.skill_code || skill.skillCode || '').trim()).filter(Boolean))),
  ), [betaReadyCurriculumAreas]);
  const betaReadyPracticeSubjects = useMemo(() => PRACTICE_SUBJECTS
    .map((subject) => ({
      ...subject,
      skills: subject.skills.filter((skill) => betaReadySkillCodes.has(String(skill.skillCode || '').trim())),
    }))
    .filter((subject) => subject.skills.length > 0), [betaReadySkillCodes]);
  const selectedSubject = betaReadyPracticeSubjects.find((subject) => subject.key === selectedSubjectKey) || betaReadyPracticeSubjects[0] || null;
  const pageState = useMemo(() => {
    const filterItems = (items) => (
      Array.isArray(items)
        ? items.filter((item) => betaReadySkillCodes.has(String(item?.skillCode || item?.skill_code || '').trim()))
        : []
    );
    const recommendations = filterItems(rawPageState.recommendations);
    const starterSuggestions = filterItems(rawPageState.starterSuggestions);
    return {
      ...rawPageState,
      recommendations,
      starterSuggestions,
      hasRecommendations: recommendations.length > 0,
      isColdStart: rawPageState.isColdStart || recommendations.length === 0,
      // The beta-ready filter can only remove recommendations, never sessions.
      // hasRecentSession is deliberately carried through unchanged so it stays
      // independent of the recomputed isColdStart above.
      hasRecentSession: rawPageState.hasRecentSession,
    };
  }, [betaReadySkillCodes, rawPageState]);
  const canPreviewUpcoming = isTutor || role === 'ADMIN';
  const builderArea = useMemo(
    () => betaReadyCurriculumAreas.find((area) => area.key === buildAreaKey) || betaReadyCurriculumAreas[0] || null,
    [buildAreaKey, betaReadyCurriculumAreas],
  );
  const builderGroups = useMemo(
    () => (Array.isArray(builderArea?.groups) ? builderArea.groups : []),
    [builderArea],
  );
  const betaReadyAllTopics = useMemo(() => allTopics
    .map((subject) => ({
      ...subject,
      skills: subject.skills.filter((skill) => betaReadySkillCodes.has(String(skill.skillCode || '').trim())),
    }))
    .filter((subject) => subject.skills.length > 0), [allTopics, betaReadySkillCodes]);
  const builderGroup = builderGroups.find((group) => group.key === buildGroupKey) || builderGroups[0] || null;
  const selectionModeAvailability = useMemo(
    () => readSelectionModeAvailability(fullCurriculum),
    [fullCurriculum],
  );
  const buildScopeState = getSelectionModeState(selectionModeAvailability, buildSelectionMode);
  const isBuildScopeAvailable = buildScopeState.available;

  useEffect(() => {
    let isMounted = true;
    if (!isStudent) {
      setIsLoadingStudentDashboard(false);
      return () => {
        isMounted = false;
      };
    }

    const loadStudentDashboard = async () => {
      setIsLoadingStudentDashboard(true);
      try {
        const response = await getMyStudentDashboard();
        const data = await parseJsonSafe(response);
        if (
          response.status === 403
          && data?.detail === 'guardian_verification_required'
        ) {
          navigate('/access-restricted?reason=guardian', { replace: true });
          return;
        }
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, 'Failed to load student practice guidance.'));
        }
        if (isMounted) {
          setStudentDashboard(data);
        }
      } catch (error) {
        if (isMounted) {
          setActionError(error.message || 'Failed to load student practice guidance.');
          setStudentDashboard(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingStudentDashboard(false);
        }
      }
    };

    loadStudentDashboard();
    return () => {
      isMounted = false;
    };
  }, [isStudent, navigate]);

  useEffect(() => {
    let isMounted = true;
    if (!isTutor) {
      setIsLoadingStudents(false);
      return () => {
        isMounted = false;
      };
    }

    const loadStudents = async () => {
      setActionError('');
      try {
        const response = await getMyTutorStudents();
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, 'Failed to load linked students.'));
        }
        const nextStudents = Array.isArray(data) ? data : [];
        if (isMounted) {
          setStudents(nextStudents);
          setSelectedStudentId((currentValue) => {
            if (currentValue && nextStudents.some((student) => String(student.id) === String(currentValue))) {
              return currentValue;
            }
            return nextStudents[0] ? String(nextStudents[0].id) : '';
          });
        }
      } catch (error) {
        if (isMounted) {
          setStudents([]);
          setActionError(error.message || 'Failed to load linked students.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingStudents(false);
        }
      }
    };

    loadStudents();
    return () => {
      isMounted = false;
    };
  }, [isTutor]);

  useEffect(() => {
    let isMounted = true;

    const loadRecentSessions = async () => {
      setRecentSessionError('');
      setIsLoadingRecent(true);
      try {
        const filters = {};
        if (isTutor && selectedStudentId) {
          filters.studentId = selectedStudentId;
        }
        const response = await getSessions(filters);
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, 'Failed to load recent sessions.'));
        }
        const sessions = Array.isArray(data) ? data : [];
        const resumableSession = sessions.find((session) => session?.status === 'ACTIVE' || !session?.ended_at) || null;
        if (isMounted) {
          setRecentSessionsList(sessions);
          setRecentSession(resumableSession);
        }
      } catch (error) {
        if (isMounted) {
          setRecentSessionsList([]);
          setRecentSession(null);
          setRecentSessionError(error.message || 'Failed to load recent sessions.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingRecent(false);
        }
      }
    };

    if (isTutor && !selectedStudentId) {
      setRecentSessionsList([]);
      setRecentSession(null);
      setIsLoadingRecent(false);
      return () => {
        isMounted = false;
      };
    }

    loadRecentSessions();
    return () => {
      isMounted = false;
    };
  }, [isTutor, selectedStudentId]);

  useEffect(() => {
    let isMounted = true;

    const loadCurriculum = async () => {
      setIsLoadingCurriculum(true);
      try {
        const response = isTutor
          ? await getStudentPracticeCurriculum(selectedStudentId)
          : await getMyPracticeCurriculum();
        const data = await parseJsonSafe(response);
        if (
          response.status === 403
          && data?.detail === 'guardian_verification_required'
        ) {
          navigate('/access-restricted?reason=guardian', { replace: true });
          return;
        }
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, 'Failed to load full curriculum.'));
        }
        if (isMounted) {
          setFullCurriculum(data);
        }
      } catch (error) {
        if (isMounted) {
          setFullCurriculum(null);
          setActionError(error.message || 'Failed to load full curriculum.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingCurriculum(false);
        }
      }
    };

    if (isTutor && !selectedStudentId) {
      setFullCurriculum(null);
      setIsLoadingCurriculum(false);
      return () => {
        isMounted = false;
      };
    }

    loadCurriculum();
    return () => {
      isMounted = false;
    };
  }, [isTutor, isStudent, navigate, selectedStudentId]);

  useEffect(() => {
    if (!betaReadyCurriculumAreas.length) {
      setBuildAreaKey('');
      setBuildGroupKey('');
      return;
    }
    if (!betaReadyCurriculumAreas.some((area) => area.key === buildAreaKey)) {
      setBuildAreaKey(betaReadyCurriculumAreas[0].key || '');
    }
  }, [buildAreaKey, betaReadyCurriculumAreas]);

  useEffect(() => {
    if (!builderGroups.length) {
      setBuildGroupKey('');
      return;
    }
    if (!builderGroups.some((group) => group.key === buildGroupKey)) {
      setBuildGroupKey(builderGroups[0].key || '');
    }
  }, [buildGroupKey, builderGroups]);

  useEffect(() => {
    if (buildSelectionMode !== 'multi_topic') {
      setSelectedTopicTargets([]);
    }
    if (buildSelectionMode !== 'unit_mix') {
      setBuildUnit('U1');
    }
  }, [buildSelectionMode]);

  const handleResumeSession = () => {
    const resumeSessionId = getSessionId(recentSession);
    if (!resumeSessionId) {
      return;
    }
    // The runtime is a property of the session's mode, never of the link that
    // was clicked: an EXAM session must not open in the tutor runtime.
    navigate(getSessionRuntimePath(resumeSessionId, getSessionRuntime(recentSession)));
  };

  const handleCreateSessionError = (error) => {
    const detailRaw = String(error?.detail || error?.message || '');
    if (
      isStudent
      && Number(error?.status) === 403
      && (
        error?.detail === 'guardian_verification_required'
        || detailRaw.includes('guardian_verification_required')
      )
    ) {
      navigate('/access-restricted?reason=guardian', { replace: true });
      return;
    }
    setActionError(getSessionStartErrorMessage(error));
  };

  // Never silently reuse and never silently end: the learner chooses. Shared
  // with the dashboard and My Students so the three call sites cannot drift.
  const {
    conflict: activeSessionConflict,
    isResolving: isResolvingActiveSession,
    guardCreate,
    resume: handleResumeActiveSession,
    endAndStartNew: handleEndActiveSessionAndStartNew,
  } = useActiveSessionConflict({
    navigate,
    onEndError: (error) => setActionError(error?.message || 'Failed to end the existing session.'),
    onRetryError: handleCreateSessionError,
  });

  const startPracticeSession = async (payload, selectionKey) => {
    if (isTutor && !selectedStudentId) {
      setActionError('Choose a linked student before starting practice.');
      return;
    }

    setActionError('');

    // The whole create, closed over the exact request the learner made, so
    // "End it and start a new session" retries it rather than a re-derived
    // approximation of it.
    const createSession = async () => {
      setPendingSelectionKey(selectionKey);
      let didNavigate = false;
      try {
        let data;
        if (isStudent) {
          data = await postCreateStudentPracticeSession(payload);
        } else {
          const response = await postCreateSession({
            student_id: Number(selectedStudentId),
            tutor_user_id: Number(user?.id),
            ...buildPracticeSessionPayload(payload),
          });
          const responsePayload = await parseJsonSafe(response);
          if (!response.ok) {
            if (response.status === 403 && responsePayload?.detail === 'guardian_verification_required') {
              throw new Error(`${GUARDIAN_UNLOCK_REQUIREMENT} ${STAFF_GUARDIAN_NEXT_STEP}`);
            }
            throw createSessionRequestError(response, responsePayload, 'Failed to start practice.');
          }
          data = responsePayload;
        }

        const createdSessionId = data?.session_id ?? data?.id;
        if (!createdSessionId) {
          throw new Error('Session created but id was missing from response.');
        }
        navigate(`/sessions/${createdSessionId}/tutor`);
        didNavigate = true;
      } finally {
        if (!didNavigate) {
          setPendingSelectionKey('');
        }
      }
    };

    try {
      await guardCreate(createSession);
    } catch (error) {
      handleCreateSessionError(error);
    }
  };

  const handleStartPractice = async (skill) => {
    const skillCode = typeof skill?.skillCode === 'string' ? skill.skillCode.trim() : '';
    const microSkillCode = typeof skill?.microSkillCode === 'string' ? skill.microSkillCode.trim() : '';
    const selectionKey = getPracticeTargetKey(skillCode, microSkillCode, skill?.focusName || skillCode);
    if (!skillCode) {
      return;
    }
    const tier = typeof skill?.tier === 'string' && skill.tier.trim() ? skill.tier.trim() : 'FOUNDATION';
    await startPracticeSession(
      {
        selectionMode: 'single_topic',
        skillCode,
        microSkillCode,
        tier,
        ...exactSessionLength,
      },
      selectionKey,
    );
  };

  const handleStartBuiltSession = async () => {
    // Reads the same server-supplied capability the option buttons render, so a
    // manipulated DOM (or a stale disabled attribute) still cannot reach the API.
    if (!buildScopeState.available) {
      setActionError(getSelectionModeUnavailableCopy(role, buildScopeState.unavailableReason));
      return;
    }
    if (buildSelectionMode === 'topic_group' && !builderGroup?.key) {
      setActionError('Choose a topic group before starting this session.');
      return;
    }
    if (buildSelectionMode === 'multi_topic' && selectedTopicTargets.length < 2) {
      setActionError('Choose at least two topics for a mixed session.');
      return;
    }
    const payload = {
      selectionMode: buildSelectionMode,
      tier: buildTier,
      ...buildSessionLength,
    };
    let selectionKey = `build:${buildSelectionMode}`;
    if (buildSelectionMode === 'topic_group') {
      payload.topicGroupCode = builderGroup.key;
      selectionKey = `group:${builderGroup.key}`;
    } else if (buildSelectionMode === 'multi_topic') {
      payload.topicTargets = selectedTopicTargets;
      selectionKey = `multi:${selectedTopicTargets.map((item) => getPracticeTargetKey(item.skillCode, item.microSkillCode, item.focusName)).join(',')}`;
    } else if (buildSelectionMode === 'unit_mix') {
      payload.unit = buildUnit;
      selectionKey = `unit:${buildUnit}`;
    }
    await startPracticeSession(payload, selectionKey);
  };

  const toggleTopicTarget = (skill) => {
    const skillCode = typeof skill?.skillCode === 'string' ? skill.skillCode.trim() : typeof skill?.skill_code === 'string' ? skill.skill_code.trim() : '';
    const microSkillCode = typeof skill?.microSkillCode === 'string' ? skill.microSkillCode.trim() : typeof skill?.micro_skill_code === 'string' ? skill.micro_skill_code.trim() : '';
    const focusName = typeof skill?.focusName === 'string' ? skill.focusName : typeof skill?.name === 'string' ? skill.name : skillCode;
    const key = getPracticeTargetKey(skillCode, microSkillCode, focusName);
    if (!key || !skillCode) {
      return;
    }
    setSelectedTopicTargets((currentValue) => {
      if (currentValue.some((target) => getPracticeTargetKey(target.skillCode, target.microSkillCode, target.focusName) === key)) {
        return currentValue.filter((target) => getPracticeTargetKey(target.skillCode, target.microSkillCode, target.focusName) !== key);
      }
      return [
        ...currentValue,
        {
          skillCode,
          ...(microSkillCode ? { microSkillCode } : {}),
          focusName,
        },
      ];
    });
  };

  if (isLoadingStudentDashboard || isLoadingRecent || isLoadingStudents || isLoadingCurriculum) {
    return (
      <div className="page page-shell page-stack">
        <section className="card">
          <p>Loading practice...</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">Practice</p>
          <h1>Practice</h1>
          <p>Choose where to focus next without losing the system guidance that keeps practice structured.</p>
        </div>
        {isTutor ? (
          <div className="practice-header-controls">
            <label className="practice-student-picker" htmlFor="practice-student-select">
              <span>Student</span>
              <select
                id="practice-student-select"
                value={selectedStudentId}
                onChange={(event) => setSelectedStudentId(event.target.value)}
                disabled={students.length === 0}
              >
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.display_name || student.student_display_name || `Student ${student.id}`}
                  </option>
                ))}
              </select>
            </label>
            <Link to="/tutor/students" className="btn btn-secondary">Open My students</Link>
          </div>
        ) : null}
      </section>

      <ActiveSessionConflictPrompt
        conflict={activeSessionConflict}
        isResolving={isResolvingActiveSession}
        onResume={handleResumeActiveSession}
        onEndAndStartNew={handleEndActiveSessionAndStartNew}
        titleId="practice-active-session-conflict-title"
      />

      {isTutor && students.length === 0 ? (
        <section className="card">
          <p className="section-title">No linked students yet</p>
          <p className="muted-text">A tutor needs one linked student selected before starting a practice session from this page.</p>
        </section>
      ) : null}

      {pageState.isColdStart ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Getting started</p>
              <h2>Start here</h2>
            </div>
          </div>
          <p className="practice-intro-copy">
            Choose a topic to get started. Your practice will help improve future recommendations, so it is fine to begin with an area that feels comfortable.
          </p>
          <StarterCards
            items={pageState.starterSuggestions}
            pendingSelectionKey={pendingSelectionKey}
            onStartPractice={handleStartPractice}
          />
        </section>
      ) : null}

      {pageState.hasRecommendations ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Guided start</p>
              <h2>Recommended for you</h2>
            </div>
          </div>
          <RecommendationCards
            items={pageState.recommendations}
            pendingSelectionKey={pendingSelectionKey}
            onStartPractice={handleStartPractice}
          />
        </section>
      ) : null}

      {pageState.hasRecentSession ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Keep momentum</p>
              <h2>Resume recent</h2>
            </div>
          </div>
          <div className="practice-resume-card">
            <div className="practice-resume-copy">
              <p className="section-title">{getRecentSessionTitle(recentSession)}</p>
              <p className="practice-resume-summary">{getRecentSessionSummary(recentSession)}</p>
            </div>
            <div className="practice-resume-actions">
              <span className={`status-pill ${getSessionStatusTone(recentSession?.status)}`}>
                {recentSession?.status === 'COMPLETED' ? 'Completed' : 'Active'}
              </span>
              <button type="button" className="btn btn-secondary" onClick={handleResumeSession}>
                Resume session
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Session builder</p>
            <h2>Build your practice session</h2>
          </div>
        </div>
        <PracticeBuilderModeSwitch
          value={builderMode}
          onChange={(nextMode) => {
            setActionError('');
            setBuilderMode(nextMode);
          }}
        />

        {builderMode === 'exact' ? (
          <div className="practice-builder-panel">
            <p className="practice-intro-copy">
              {audienceCopy.exactTopicDescription}
            </p>
            <SessionLengthSelector
              value={exactSessionLength}
              onChange={setExactSessionLength}
              name="exact-practice-session-length"
              description="Choose how long this exact-topic session should run."
            />
            <div className="practice-builder-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={isLoadingCurriculum || (isTutor && !selectedStudentId)}
                onClick={(event) => {
                  setActionError('');
                  curriculumTriggerRef.current = event.currentTarget;
                  setIsCurriculumSelectorOpen(true);
                }}
              >
                {isLoadingCurriculum ? 'Loading topics...' : 'Choose exact topic'}
              </button>
            </div>
          </div>
        ) : (
          <div className="practice-builder-panel">
            <fieldset className="practice-builder-fieldset">
              <legend>Session scope</legend>
              <div className="practice-builder-option-grid">
                {BUILDER_SCOPE_OPTIONS.map((option) => {
                  const modeState = getSelectionModeState(selectionModeAvailability, option.key);
                  const isDisabled = !modeState.available;
                  const reasonId = `practice-builder-scope-reason-${option.key}`;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      className={`practice-builder-option${buildSelectionMode === option.key ? ' is-active' : ''}`}
                      aria-pressed={buildSelectionMode === option.key}
                      disabled={isDisabled}
                      aria-describedby={isDisabled ? reasonId : undefined}
                      onClick={() => {
                        setActionError('');
                        setBuildSelectionMode(option.key);
                      }}
                    >
                      <strong>{option.label}</strong>
                      <span id={isDisabled ? reasonId : undefined}>
                        {isDisabled
                          ? getSelectionModeUnavailableCopy(role, modeState.unavailableReason)
                          : option.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {buildSelectionMode === 'topic_group' ? (
              <div className="practice-builder-scope-layout">
                <fieldset className="practice-builder-fieldset">
                  <legend>Area</legend>
                  <div className="practice-builder-pill-row">
                    {betaReadyCurriculumAreas.map((area) => (
                      <button
                        key={area.key}
                        type="button"
                        className={`practice-builder-pill${builderArea?.key === area.key ? ' is-active' : ''}`}
                        aria-pressed={builderArea?.key === area.key}
                        onClick={() => {
                          setActionError('');
                          setBuildAreaKey(area.key);
                          setBuildGroupKey(area.groups?.[0]?.key || '');
                        }}
                      >
                        {area.label}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <fieldset className="practice-builder-fieldset">
                  <legend>Topic group</legend>
                  <div className="practice-builder-option-list">
                    {builderGroups.map((group) => (
                      <button
                        key={group.key}
                        type="button"
                        className={`practice-builder-option${builderGroup?.key === group.key ? ' is-active' : ''}`}
                        aria-pressed={builderGroup?.key === group.key}
                        onClick={() => {
                          setActionError('');
                          setBuildGroupKey(group.key);
                        }}
                      >
                        <strong>{group.label}</strong>
                        <span>{group.description || `${group.skills?.length || 0} topics available.`}</span>
                      </button>
                    ))}
                  </div>
                </fieldset>
              </div>
            ) : null}

            {buildSelectionMode === 'multi_topic' ? (
              <div className="practice-builder-scope-layout">
                <fieldset className="practice-builder-fieldset">
                  <legend>Area</legend>
                  <div className="practice-builder-pill-row">
                    {betaReadyCurriculumAreas.map((area) => (
                      <button
                        key={area.key}
                        type="button"
                        className={`practice-builder-pill${builderArea?.key === area.key ? ' is-active' : ''}`}
                        aria-pressed={builderArea?.key === area.key}
                        onClick={() => {
                          setActionError('');
                          setBuildAreaKey(area.key);
                          setBuildGroupKey(area.groups?.[0]?.key || '');
                        }}
                      >
                        {area.label}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <fieldset className="practice-builder-fieldset">
                  <legend>Topic group</legend>
                  <div className="practice-builder-option-list">
                    {builderGroups.map((group) => (
                      <button
                        key={group.key}
                        type="button"
                        className={`practice-builder-option${builderGroup?.key === group.key ? ' is-active' : ''}`}
                        aria-pressed={builderGroup?.key === group.key}
                        onClick={() => {
                          setActionError('');
                          setBuildGroupKey(group.key);
                        }}
                      >
                        <strong>{group.label}</strong>
                        <span>{group.description || `${group.skills?.length || 0} topics available.`}</span>
                      </button>
                    ))}
                  </div>
                </fieldset>
                <fieldset className="practice-builder-fieldset">
                  <legend>Selected topics</legend>
                  {selectedTopicTargets.length ? (
                    <div className="practice-builder-selection-summary">
                      {selectedTopicTargets.map((target) => (
                        <span key={getPracticeTargetKey(target.skillCode, target.microSkillCode, target.focusName)} className="practice-builder-chip">
                          {target.focusName}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="muted-text">Choose at least two topics for a mixed session.</p>
                  )}
                  <div className="practice-skill-list practice-builder-topic-list">
                    {(builderGroup?.skills || []).map((skill) => {
                      const key = getPracticeTargetKey(skill.skill_code, skill.micro_skill_code, skill.name);
                      const isSelected = selectedTopicTargets.some((target) => getPracticeTargetKey(target.skillCode, target.microSkillCode, target.focusName) === key);
                      return (
                        <article key={key} className="practice-skill-item">
                          <div className="practice-skill-copy">
                            <div className="practice-card-header">
                              <strong>{skill.name}</strong>
                              {skill.status ? (
                                <span className={`status-pill ${getStatusClass(skill.status)}`}>{getStatusLabel(skill.status)}</span>
                              ) : null}
                            </div>
                            <p className="practice-curriculum-skill-description">{skill.description}</p>
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => toggleTopicTarget(skill)}
                          >
                            {isSelected ? 'Remove' : 'Add'}
                          </button>
                        </article>
                      );
                    })}
                  </div>
                </fieldset>
              </div>
            ) : null}

            {buildSelectionMode === 'unit_mix' ? (
              <fieldset className="practice-builder-fieldset">
                <legend>Unit</legend>
                <div className="practice-builder-pill-row">
                  {BUILDER_UNITS.map((unit) => (
                    <button
                      key={unit}
                      type="button"
                      className={`practice-builder-pill${buildUnit === unit ? ' is-active' : ''}`}
                      aria-pressed={buildUnit === unit}
                      onClick={() => {
                        setActionError('');
                        setBuildUnit(unit);
                      }}
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              </fieldset>
            ) : null}

            {buildSelectionMode === 'review_mix' ? (
              <div className="practice-builder-review-note">
                <p>Review mix builds a broader session from review-due, weak, and in-progress areas for this student.</p>
              </div>
            ) : null}

            <div className="practice-builder-settings-grid">
              <SessionLengthSelector
                value={buildSessionLength}
                onChange={setBuildSessionLength}
                name="built-practice-session-length"
                description="Choose how long this built session should run."
              />
              <TierSelector value={buildTier} onChange={setBuildTier} />
            </div>

            <div className="practice-builder-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStartBuiltSession}
                disabled={
                  pendingSelectionKey.startsWith('build:')
                  || pendingSelectionKey.startsWith('group:')
                  || pendingSelectionKey.startsWith('multi:')
                  || pendingSelectionKey.startsWith('unit:')
                  || !isBuildScopeAvailable
                  || (buildSelectionMode === 'topic_group' && !builderGroup?.key)
                  || (buildSelectionMode === 'multi_topic' && selectedTopicTargets.length < 2)
                  || (isTutor && !selectedStudentId)
                }
              >
                {(
                  pendingSelectionKey.startsWith('build:')
                  || pendingSelectionKey.startsWith('group:')
                  || pendingSelectionKey.startsWith('multi:')
                  || pendingSelectionKey.startsWith('unit:')
                ) ? 'Starting...' : 'Start session'}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Free choice</p>
            <h2>Choose what to practise</h2>
          </div>
        </div>

        <div className="practice-subject-grid" role="tablist" aria-label="Practice subjects">
          {betaReadyPracticeSubjects.map((subject) => (
            <button
              key={subject.key}
              type="button"
              role="tab"
              aria-selected={selectedSubject.key === subject.key}
              className={`practice-subject-card${selectedSubject.key === subject.key ? ' is-active' : ''}`}
              onClick={() => setSelectedSubjectKey(subject.key)}
            >
              <strong>{subject.label}</strong>
              <span>{subject.description}</span>
            </button>
          ))}
        </div>

        <div className="practice-skill-panel" role="tabpanel" aria-label={`${selectedSubject?.label || 'Beta-ready'} skills`}>
          {selectedSubject ? (
            <div className="practice-skill-list">
              {selectedSubject.skills.map((skill) => (
              <article key={skill.skillCode} className="practice-skill-item">
                <div className="practice-skill-copy">
                  <div className="practice-card-header">
                    <strong>{skill.focusName}</strong>
                    {!pageState.isColdStart ? (
                      <span className={`status-pill ${getStatusClass(skill.status)}`}>{getStatusLabel(skill.status)}</span>
                    ) : null}
                  </div>
                  <p className="muted-text">{pageState.isColdStart ? 'A sensible place to begin if you want to explore this area.' : skill.recency}</p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleStartPractice(skill)}
                  disabled={pendingSelectionKey === skill.skillCode || (isTutor && !selectedStudentId)}
                >
                  {pendingSelectionKey === skill.skillCode ? 'Starting...' : 'Start practice'}
                </button>
              </article>
              ))}
            </div>
          ) : (
            <p className="muted-text">Use the full curriculum selector for the topics currently available in Beta.</p>
          )}
        </div>
        <div className="practice-curriculum-cta-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={(event) => {
              curriculumTriggerRef.current = event.currentTarget;
              setIsCurriculumSelectorOpen(true);
            }}
            disabled={isTutor && !selectedStudentId}
          >
            Select from full curriculum
          </button>
        </div>
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">More choice</p>
            <h2>Browse all topics</h2>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsAllTopicsOpen((value) => !value)}
          >
            {isAllTopicsOpen ? 'Hide all topics' : 'See all topics'}
          </button>
        </div>
        <p className="muted-text">
          Browse the surfaced topic set when you want something outside the guided suggestions, or open the full curriculum selector for exact control.
        </p>
        {isAllTopicsOpen ? (
          <div className="practice-all-topics">
            {betaReadyAllTopics.map((subject) => (
              <section key={subject.key} className="practice-topic-group">
                <div className="practice-topic-group-header">
                  <h3>{subject.label}</h3>
                  <p className="muted-text">{subject.description}</p>
                </div>
                <div className="practice-skill-list">
                  {subject.skills.map((skill) => (
                    <article key={`${subject.key}-${skill.skillCode}`} className="practice-skill-item">
                      <div className="practice-skill-copy">
                        <div className="practice-card-header">
                          <strong>{skill.focusName}</strong>
                          {!pageState.isColdStart ? (
                            <span className={`status-pill ${getStatusClass(skill.status)}`}>{getStatusLabel(skill.status)}</span>
                          ) : null}
                        </div>
                        <p className="muted-text">
                          {pageState.isColdStart
                            ? 'Start anywhere that feels manageable. We will use this to improve what comes next.'
                            : skill.recency}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleStartPractice(skill)}
                        disabled={pendingSelectionKey === skill.skillCode || (isTutor && !selectedStudentId)}
                      >
                        {pendingSelectionKey === skill.skillCode ? 'Starting...' : 'Start practice'}
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : null}
      </section>

      {recentSessionError ? <p role="alert" className="notice notice-error">{recentSessionError}</p> : null}
      {actionError && !isCurriculumSelectorOpen ? (
        <p role="alert" className="notice notice-error">{actionError}</p>
      ) : null}

      <CurriculumSelectorDialog
        isOpen={isCurriculumSelectorOpen}
        isLoading={isLoadingCurriculum}
        areas={betaReadyCurriculumAreas}
        upcomingAreas={upcomingAreas}
        canPreviewUpcoming={canPreviewUpcoming}
        isColdStart={pageState.isColdStart}
        pendingSelectionKey={pendingSelectionKey}
        canStartPractice={!isTutor || Boolean(selectedStudentId)}
        actionError={actionError}
        onClose={() => setIsCurriculumSelectorOpen(false)}
        onStartPractice={handleStartPractice}
        restoreFocusRef={curriculumTriggerRef}
      />
    </div>
  );
}

export default PracticePage;
